import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowUpCircle,
  CheckCircle2,
  DatabaseBackup,
  HardDrive,
  History,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Server,
  Sparkles,
} from 'lucide-react';
import {
  fetchSauvegardes,
  mettreAJourSchema,
  restaurerCoffre,
  restaurerSauvegarde,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ModaleEspace from '../components/ModaleEspace';

import { dateFr } from '../utils/dates';
const poids = (octets: number): string => {
  const unites = ['o', 'Ko', 'Mo', 'Go'];
  let rang = 0;
  let valeur = octets;
  while (valeur >= 1024 && rang < unites.length - 1) {
    valeur /= 1024;
    rang += 1;
  }
  return `${valeur.toLocaleString('fr-FR', {
    maximumFractionDigits: valeur >= 10 || rang === 0 ? 0 : 1,
  })} ${unites[rang]}`;
};

/** Une jauge d'occupation : verte, puis ambre, puis rouge à l'approche du plein. */
function Jauge({ part }: { part: number }) {
  const couleur = part >= 95 ? 'bg-red-500' : part >= 85 ? 'bg-amber-500' : 'bg-primary-600';

  return (
    <div className="mt-2 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <div className={`h-full ${couleur} transition-all`} style={{ width: `${Math.min(100, part)}%` }} />
    </div>
  );
}

/**
 * Les sauvegardes et la place occupée.
 *
 * Deux questions qu'un président ne se pose que trop tard : « mes données
 * sont-elles à l'abri ? » et « me reste-t-il de la place ? ». La page y répond
 * avant qu'elles ne se posent — la sauvegarde de chaque nuit, gardée une
 * semaine, et deux compteurs.
 */
export default function SauvegardesPage() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.roles?.includes('ROLE_ADMIN') === true;
  const query = useQuery({ queryKey: ['sauvegardes'], queryFn: fetchSauvegardes, enabled: isAdmin });

  const [extensionOuverte, setExtensionOuverte] = useState(false);
  const [choisie, setChoisie] = useState<string | null>(null);
  const [coffreChoisi, setCoffreChoisi] = useState<string | null>(null);
  const [motDePasse, setMotDePasse] = useState('');
  const [avis, setAvis] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const restaurer = useMutation({
    mutationFn: () => restaurerSauvegarde(choisie as string, motDePasse),
    onSuccess: (d: any) => {
      setAvis({ type: 'success', message: d.message });
      setChoisie(null);
      setMotDePasse('');
      queryClient.invalidateQueries();
    },
    onError: (e: Error) => setAvis({ type: 'error', message: e.message }),
  });

  const restaurerLeCoffre = useMutation({
    mutationFn: () => restaurerCoffre(coffreChoisi as string, motDePasse),
    onSuccess: (d: any) => {
      setAvis({ type: 'success', message: d.message });
      setCoffreChoisi(null);
      setMotDePasse('');
      queryClient.invalidateQueries();
    },
    onError: (e: Error) => setAvis({ type: 'error', message: e.message }),
  });

  const mettreANiveau = useMutation({
    mutationFn: () => mettreAJourSchema(),
    onSuccess: (d: any) => {
      setAvis({ type: 'success', message: d.message });
      queryClient.invalidateQueries({ queryKey: ['sauvegardes'] });
    },
    onError: (e: Error) => setAvis({ type: 'error', message: e.message }),
  });

  if (authLoading || (isAdmin && query.isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">Cette page est réservée aux administrateurs.</p>
          </div>
        </div>
      </div>
    );
  }

  const donnees = query.data;
  const sauvegardes: { date: string; octets: number; version: string | null }[] =
    donnees?.sauvegardes ?? [];
  const base = donnees?.quotas?.base;
  const documents = donnees?.quotas?.documents;
  const forfait = donnees?.forfait;
  const messagerie = donnees?.messagerie;
  const coffre: {
    copies: { fichier: string; octets: number; quand: string; chiffre: boolean }[];
    intervalleHeures: number;
    exemplaires: number;
  } | undefined = donnees?.coffre;

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <DatabaseBackup className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Sauvegardes</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Vos données sont sauvegardées chaque nuit, et conservées sept jours.
          </p>
        </div>
      </div>

      {avis && (
        <div
          className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${
            avis.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {avis.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span>{avis.message}</span>
        </div>
      )}

      {/* Une base revenue d'avant-hier porte le schéma d'avant-hier, et le
          code, lui, n'a pas reculé : c'est le geste qui rattrape l'écart. */}
      {donnees?.migrationsEnAttente > 0 && donnees?.peutRestaurer && (
        <section className="card lg:p-6 border-amber-200 bg-amber-50/70 dark:bg-amber-900/10 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <ArrowUpCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                Votre base est en retard sur l'application
              </h2>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {donnees.migrationsEnAttente} mise{donnees.migrationsEnAttente > 1 ? 's' : ''} à jour
                {donnees.migrationsEnAttente > 1 ? ' sont' : ' est'} en attente — c'est normal après
                avoir restauré une sauvegarde ancienne. L'application sert la version{' '}
                <strong>{donnees.version ?? '—'}</strong>&nbsp;: remettez la base à niveau pour
                retrouver toutes les fonctions.
              </p>
              <button
                onClick={() => mettreANiveau.mutate()}
                disabled={mettreANiveau.isPending}
                className="btn-primary inline-flex items-center gap-2 mt-3 text-sm disabled:opacity-60"
              >
                {mettreANiveau.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Mettre à jour
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Les trois compteurs : ce qui est occupé, ce qui reste. */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card lg:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900 dark:text-gray-100">Base de données</span>
            </div>
            <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {base ? `${base.part} %` : '—'}
            </span>
          </div>
          <Jauge part={base?.part ?? 0} />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {base ? (
              <>
                {poids(base.utilise)} occupés sur {poids(base.total)} —{' '}
                <strong className="text-gray-900 dark:text-gray-100">
                  {poids(Math.max(0, base.total - base.utilise))} disponibles
                </strong>
              </>
            ) : (
              '—'
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Vos membres, événements, factures, dons et réservations.
          </p>
        </div>

        <div className="card lg:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900 dark:text-gray-100">Documents</span>
            </div>
            <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {documents ? `${documents.part} %` : '—'}
            </span>
          </div>
          <Jauge part={documents?.part ?? 0} />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {documents ? (
              <>
                {poids(documents.utilise)} occupés sur {poids(documents.total)} —{' '}
                <strong className="text-gray-900 dark:text-gray-100">
                  {poids(Math.max(0, documents.total - documents.utilise))} disponibles
                </strong>
              </>
            ) : (
              '—'
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Visuels des événements, logo, justificatifs d'achat et pièces jointes.
          </p>
        </div>
      </section>

      {/* Les boîtes mail : le troisième compteur, et le plus traître. Une
          boîte pleine refuse le courrier sans prévenir personne — ni celui
          qui écrit, ni celui qui attend. */}
      {messagerie?.disponible && (
        <section className="card lg:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary-600" />
              <span className="font-semibold text-gray-900 dark:text-gray-100">Boîtes mail</span>
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {poids(messagerie.quota)} par boîte
            </span>
          </div>

          <div className="mt-4 space-y-3">
            {messagerie.boites.map((b: any) => (
              <div key={b.adresse}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-300 truncate">{b.adresse}</span>
                  <span className="tabular-nums shrink-0 text-gray-600 dark:text-gray-400">
                    {poids(b.utilise)} / {poids(b.total)}
                  </span>
                </div>
                <Jauge part={b.part} />
              </div>
            ))}
          </div>

          {messagerie.sature ? (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2.5 text-sm text-red-800 dark:text-red-200">
              <p className="font-semibold">Une boîte est pleine.</p>
              <p className="mt-1">
                Elle refuse le courrier qui arrive, et l'expéditeur n'en est pas toujours averti.
                Faites d'abord le ménage : les messages avec pièces jointes lourdes — photos,
                PDF de plusieurs mégaoctets — occupent à eux seuls l'essentiel de la place. Videz
                aussi la corbeille et le dossier des éléments envoyés, qui gardent une copie de
                chaque pièce que vous avez expédiée.
              </p>
            </div>
          ) : messagerie.alerte ? (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200">
              Une boîte approche de sa limite. Un ménage des messages à pièces jointes lourdes, de
              la corbeille et des éléments envoyés suffit le plus souvent à faire de la place.
            </div>
          ) : (
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Une boîte pleine refuse le courrier entrant sans prévenir personne : ce relevé est là
              pour que cela ne vous arrive pas.
            </p>
          )}
        </section>
      )}

      {/* L'extension ne paraît qu'à l'approche de la limite : la proposer à un
          espace vide serait de la réclame. */}
      {donnees?.alerte && forfait && (
        <section className="card lg:p-6 border-amber-200 bg-amber-50/70 dark:bg-amber-900/10 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                {messagerie?.sature ? 'Une boîte mail est pleine' : 'Votre espace se remplit'}
              </h2>
              {messagerie?.sature && (
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  Commencez par le ménage décrit plus haut : c'est gratuit et souvent suffisant. Si
                  la place manque vraiment, une extension prend le relais.
                </p>
              )}
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {forfait.extensions > 0 ? (
                  <>
                    Vous en avez déjà {forfait.extensions === 1 ? 'une' : `${forfait.extensions}`}. Une
                    de plus porterait
                  </>
                ) : (
                  <>Pour continuer sans rien effacer, une extension porte</>
                )}{' '}
                votre base à <strong>{poids(forfait.base)}</strong>, vos documents à{' '}
                <strong>{poids(forfait.documents)}</strong> et chacune de vos boîtes mail à{' '}
                <strong>{poids(forfait.mail)}</strong>, pour{' '}
                <strong>{forfait.prixMensuel} €/mois</strong> ou{' '}
                <strong>{forfait.prixAnnuel} €/an</strong>.
              </p>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                L'extension se reprend autant de fois que nécessaire : chacune ajoute{' '}
                {poids(forfait.ajoutBase)} de base, {poids(forfait.ajoutDocuments)} de documents et{' '}
                {poids(forfait.ajoutMail)} par boîte.
              </p>
              <button
                onClick={() => setExtensionOuverte(true)}
                className="btn-primary inline-flex items-center gap-2 mt-3 text-sm"
              >
                Voir l'extension
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="card lg:p-6">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Sept derniers jours</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Une sauvegarde complète est prise chaque nuit, puis relue aussitôt — une sauvegarde qu'on
          n'a jamais rouverte n'est pas une sauvegarde, c'est un fichier dont on espère qu'elle en
          est une.{' '}
          {donnees?.peutRestaurer
            ? "Vous pouvez en restaurer une : l'état d'avant est mis de côté au passage, et votre mot de passe vous sera redemandé."
            : "Seul le président peut en restaurer une : c'est sa responsabilité, pas une manœuvre courante."}
        </p>

        {/* Ce que le chiffrement protège, et ce qu'il ne protège pas. Un
            cadenas sans phrase laisserait croire à davantage. */}
        {donnees?.chiffrement && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Les archives sont chiffrées sur le disque : emportées sans leur clé, elles ne se lisent
            pas. Cela protège d'un disque volé ou d'une copie égarée, non de quelqu'un qui aurait
            déjà l'accès au serveur — la sauvegarde de nuit doit pouvoir chiffrer sans personne
            pour saisir un mot de passe.
          </p>
        )}

        {sauvegardes.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Aucune sauvegarde pour l'instant — la première sera prise cette nuit.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
            {sauvegardes.map((s, rang) => (
              <li key={s.date} className="flex items-center justify-between gap-3 py-2.5">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-primary-500 shrink-0" aria-hidden="true" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {new Date(s.date + 'T12:00:00').toLocaleDateString('fr-FR', {
                      timeZone: 'Europe/Paris',
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </span>
                  {rang === 0 && (
                    <span className="text-xs rounded-full px-2 py-0.5 bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">
                      la plus récente
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-3 shrink-0">
                  {/* La version qu'elle porte : sans elle, on restaure à
                      l'aveugle et l'on découvre le décalage de schéma en
                      voyant l'application tomber. */}
                  <span
                    className={`text-xs rounded-full px-2 py-0.5 tabular-nums ${
                      s.version && s.version !== donnees?.version
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                    title={
                      s.version
                        ? `Version servie cette nuit-là : ${s.version}`
                        : 'Sauvegarde prise avant que la version ne soit notée'
                    }
                  >
                    {s.version ? `v${s.version}` : 'version inconnue'}
                  </span>
                  <span className="text-sm tabular-nums text-gray-600 dark:text-gray-400">
                    {poids(s.octets)}
                  </span>
                  {donnees?.peutRestaurer && (
                    <button
                      onClick={() => {
                        setChoisie(s.date);
                        setMotDePasse('');
                        setAvis(null);
                      }}
                      className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
                    >
                      <History className="w-4 h-4" />
                      Restaurer
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Le coffre, à part.

          Il se sauvegarde depuis sa mise en place, toutes les six heures,
          séparément de la base — mais rien ne le montrait ici, et rien ne
          permettait de le restaurer. Une sauvegarde qu'on ne peut pas
          restaurer n'est pas une sauvegarde, c'est un fichier. */}
      {coffre && (
        <section className="card lg:p-6">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary-600 shrink-0" />
            Le coffre-fort, sauvegardé à part
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Les mots de passe du bureau ont leur propre jeu de copies, prises toutes les{' '}
            {coffre.intervalleHeures} heures et conservées en {coffre.exemplaires} exemplaires — soit{' '}
            {coffre.intervalleHeures * (coffre.exemplaires - 1)} heures en remontant. C'est ce qui
            permet de rendre un identifiant supprimé par erreur sans rendre avec lui la comptabilité
            et les inscriptions de la veille.
          </p>

          {coffre.copies.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Aucune copie pour l'instant — la prochaine sera prise dans les six heures.
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
              {coffre.copies.map((c, rang) => (
                <li key={c.fichier} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-primary-500 shrink-0" aria-hidden="true" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(c.quand).toLocaleString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Europe/Paris',
                      })}
                    </span>
                    {rang === 0 && (
                      <span className="text-xs rounded-full px-2 py-0.5 bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">
                        la plus récente
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-3 shrink-0">
                    {c.chiffre && (
                      <span
                        className="text-xs rounded-full px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                        title="Chiffrée sur le disque"
                      >
                        chiffrée
                      </span>
                    )}
                    <span className="text-sm tabular-nums text-gray-600 dark:text-gray-400">
                      {poids(c.octets)}
                    </span>
                    {donnees?.peutRestaurer && (
                      <button
                        onClick={() => {
                          setCoffreChoisi(c.fichier);
                          setMotDePasse('');
                          setAvis(null);
                        }}
                        className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
                      >
                        <History className="w-4 h-4" />
                        Restaurer
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* La confirmation : c'est la seule action du logiciel qui efface le
          travail de plusieurs jours d'un clic, et le compte reste souvent
          ouvert sur un poste partagé. */}
      {choisie !== null && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Restaurer la sauvegarde du {dateFr(choisie + 'T00:00:00')} ?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Toutes les données saisies depuis cette nuit-là seront perdues : adhésions, factures,
              réservations, comptes rendus. L'état actuel est mis de côté avant la bascule, mais sa
              récupération demande le support.
            </p>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Les documents déposés, eux, ne bougent pas : seule la base revient en arrière.
            </p>

            <label className="block mt-4 text-sm text-gray-700 dark:text-gray-300">
              Votre mot de passe
              <input
                type="password"
                autoFocus
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
                className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
              />
            </label>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setChoisie(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
              >
                Annuler
              </button>
              <button
                onClick={() => restaurer.mutate()}
                disabled={restaurer.isPending || motDePasse === ''}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60"
              >
                {restaurer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Restaurer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* La restauration du coffre a sa propre confirmation : elle ne touche
          que les mots de passe, et le dire évite qu'on la craigne comme une
          restauration de base — ou, pire, qu'on la confonde avec elle. */}
      {coffreChoisi !== null && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Restaurer le coffre-fort ?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Les mots de passe du bureau reviennent à l'état de cette copie. Ceux ajoutés depuis
              seront perdus, ceux supprimés depuis reviendront.
            </p>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              <strong>Rien d'autre ne bouge</strong> : la comptabilité, les adhésions, les
              réservations et les documents restent exactement où ils sont.
            </p>

            <label className="block mt-4 text-sm text-gray-700 dark:text-gray-300">
              Votre mot de passe
              <input
                type="password"
                autoFocus
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
                className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
              />
            </label>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setCoffreChoisi(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
              >
                Annuler
              </button>
              <button
                onClick={() => restaurerLeCoffre.mutate()}
                disabled={restaurerLeCoffre.isPending || motDePasse === ''}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60"
              >
                {restaurerLeCoffre.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Restaurer le coffre
              </button>
            </div>
          </div>
        </div>
      )}

      <ModaleEspace ouverte={extensionOuverte} onFermer={() => setExtensionOuverte(false)} />
    </div>
  );
}
